import React, { useMemo, useCallback, useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	FilterFields,
	DEFAULT_LIMIT,
} from '@atlassian/jira-projects-directory-v3-utils/src/constants.tsx';
import type { ActionsCell900BadPattern$key } from '@atlassian/jira-relay/src/__generated__/ActionsCell900BadPattern.graphql';
import type { ResultsTable$key } from '@atlassian/jira-relay/src/__generated__/ResultsTable.graphql';
import { SimpleTable } from '@atlassian/jira-simple-table/src/index.tsx';
import type {
	SortOrder,
	RowType,
	OnSort as OnSortCallback,
} from '@atlassian/jira-simple-table/src/types.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { useFilter } from '../../../../controllers/filter-state/index.tsx';
import messages from './messages.tsx';
import { getTableHeader } from './table-head/index.tsx';
import { getTableRow, type projectRefType } from './table-rows/index.tsx';

type Props = {
	shouldShowCategories: boolean;
	isAdminSettingsContext: boolean;
	projectsRef: ResultsTable$key;
	jiraQueryRef: ActionsCell900BadPattern$key | null;
};

export const ResultsTable = ({
	shouldShowCategories,
	projectsRef,
	jiraQueryRef,
	isAdminSettingsContext,
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const isAnonymous = useIsAnonymous();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsEvent = createAnalyticsEvent({
		action: 'changed',
		actionSubject: 'resultsTable',
	});

	const [filter, { updateFilter }] = useFilter();

	/* eslint-disable @atlassian/relay/must-colocate-fragment-spreads */
	const projects = useFragment(
		graphql`
			fragment ResultsTable on JiraProjectConnection
			@argumentDefinitions(isAdminSettingsContext: { type: "Boolean!" }) {
				edges {
					node {
						...FavoriteCellFavouriteCell
						...NameCell
						...KeyCell
						...ProjectTypeCell
						...ProjectCategoryCell
						...LeadCell
						...LastIssueUpdateCell @include(if: $isAdminSettingsContext)
						...UrlCellUrlCell
						...ActionsCell237BadPattern
					}
				}
				totalCount
				pageInfo {
					hasNextPage
				}
			}
		`,
		projectsRef,
	);

	const head = useMemo(
		() =>
			getTableHeader({
				formatMessage,
				isAnonymous,
				shouldShowCategories,
				isAdminSettingsContext,
			}),
		[formatMessage, isAnonymous, shouldShowCategories, isAdminSettingsContext],
	);

	const projectsItems = useMemo(() => {
		const totalCount = projects?.totalCount || 0;
		return projects?.pageInfo?.hasNextPage
			? projects?.edges
			: projects?.edges?.slice((totalCount % DEFAULT_LIMIT) * -1);
	}, [projects?.edges, projects?.pageInfo?.hasNextPage, projects?.totalCount]);

	const rows = useMemo(
		() =>
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			projectsItems
				?.map(
					(project) =>
						project &&
						getTableRow(
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							project.node as projectRefType,
							isAdminSettingsContext,
							jiraQueryRef,
						),
				)
				.filter(Boolean) as RowType[],
		[projectsItems, isAdminSettingsContext, jiraQueryRef],
	);

	const handleSort = useCallback<OnSortCallback>(
		({ key, sortOrder }) => {
			updateFilter({
				[FilterFields.SortOrder]: sortOrder,
				[FilterFields.SortKey]: key,
			});
			fireUIAnalytics(analyticsEvent, 'SortOrder', { sortOrder, sortKey: key });
		},
		[analyticsEvent, updateFilter],
	);

	useEffect(() => {
		// This change has been added to resolve https://jplat.atlassian.net/browse/BLU-4931
		handleSort({
			key: filter[FilterFields.SortKey],
			// This suppression is consistent with existing code when the fix was added
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			sortOrder: filter[FilterFields.SortOrder] as SortOrder,
		});
		// We just want this to run once on component mount, so we don't need to add `filter` to the dependencies
		// Which is why we're suppressing the warning
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getCaptionText = () => {
		const tableCaptionSpecificProductSpecificCategoriesGalaxia = expVal(
			'project-terminology-refresh',
			'isEnabled',
			false,
		)
			? messages.tableCaptionSpecificProductSpecificCategoriesGalaxia
			: messages.tableCaptionSpecificProductSpecificCategories;
		const tableCaptionSpecificProductAllCategoriesGalaxia = expVal(
			'project-terminology-refresh',
			'isEnabled',
			false,
		)
			? messages.tableCaptionSpecificProductAllCategoriesGalaxia
			: messages.tableCaptionSpecificProductAllCategories;
		const tableCaptionSpecificCategoryGalaxia = expVal(
			'project-terminology-refresh',
			'isEnabled',
			false,
		)
			? messages.tableCaptionSpecificCategoryGalaxia
			: messages.tableCaptionSpecificCategory;
		const tableCaptionSpecificProductGalaxia = expVal(
			'project-terminology-refresh',
			'isEnabled',
			false,
		)
			? messages.tableCaptionSpecificProductGalaxia
			: messages.tableCaptionSpecificProduct;
		const tableCaptionAllProjectsAndAllCategoriesGalaxia = expVal(
			'project-terminology-refresh',
			'isEnabled',
			false,
		)
			? messages.tableCaptionAllProjectsAndAllCategoriesGalaxia
			: messages.tableCaptionAllProjectsAndAllCategories;
		const tableCaptionAllProjectsGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
			? messages.tableCaptionAllProjectsGalaxia
			: messages.tableCaptionAllProjects;
		const selectedProductsKeys = filter[FilterFields.ProjectType]?.split(',').filter(Boolean) || [];
		const selectedProjectCategory = filter[FilterFields.ProjectCategoryLabel];
		const selectedProductsLabels = filter[FilterFields.ProjectTypeLabel];

		if (!selectedProductsKeys.length) {
			if (!shouldShowCategories) {
				return formatMessage(tableCaptionAllProjectsGalaxia);
			}
			if (!selectedProjectCategory) {
				return formatMessage(tableCaptionAllProjectsAndAllCategoriesGalaxia);
			}
			return formatMessage(tableCaptionSpecificCategoryGalaxia, {
				category: selectedProjectCategory,
			});
		}
		if (!shouldShowCategories) {
			return formatMessage(tableCaptionSpecificProductGalaxia, {
				product: selectedProductsLabels,
			});
		}
		if (!selectedProjectCategory) {
			return formatMessage(tableCaptionSpecificProductAllCategoriesGalaxia, {
				product: selectedProductsLabels,
			});
		}
		return formatMessage(tableCaptionSpecificProductSpecificCategoriesGalaxia, {
			product: selectedProductsLabels,
			category: selectedProjectCategory,
		});
	};

	return (
		<SimpleTable
			captionText={getCaptionText()}
			head={head}
			rows={rows}
			sortKey={filter[FilterFields.SortKey]}
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			sortOrder={filter[FilterFields.SortOrder] as SortOrder}
			onSort={handleSort}
		/>
	);
};
